import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { CreateTVChannelRequest, Category, TVGuide, URLType } from '@/types/model';
import { useTranslation } from '@/hooks';
import { useCategoriesForDropdown, useTVGuidesForDropdown } from '@/hooks';
import { Translation } from '@/locales';
import { useBranding } from '@/context/BrandingContext';
import Input from '@/components/ui/Input';
import ApiSelect from '@/components/ui/ApiSelect';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect.tsx';
import CountryDropdown from '@/components/ui/CountryDropdown';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import ImageNotAvailable from '@/assets/image-not-available.png';
import { MediaSource } from '@/types/media';

const createTVChannelSchema = (t: Translation) => z.object({
  channelNumber: z
    .number({
      message: t.tvChannels.validation.channelNumberRequired
    })
    .min(0, t.tvChannels.validation.channelNumberMin),
  countryId: z
    .string()
    .optional(),
  title: z
    .string()
    .min(1, t.validation.minLength(1))
    .max(255, t.validation.maxLength(255)),
  tvGuideId: z
    .string()
    .optional(),
  categories: z
    .array(z.string())
    .optional(),
  epgId: z
    .string()
    .max(1000, t.tvChannels.validation.epgIdMax),
  imageUrl: z
    .url({
      message: t.tvChannels.validation.imageUrlInvalid
    })
    .or(z.literal('')),
  active: z.boolean(),
  adultContent: z.boolean(),
  kidsContent: z.boolean(),
  urlType: z.enum(['INTERNAL', 'EXTERNAL']),
  url: z.string().min(1, t.tvChannels.validation.urlRequired)
}).refine((data) => {
  if (data.urlType === 'EXTERNAL') {
    try {
      new URL(data.url);
      return true;
    } catch {
      return false;
    }
  } else {
    return !isNaN(Number(data.url));
  }
}, {
  message: 'Invalid URL format',
  path: ['url']
});

type TVChannelFormData = z.infer<ReturnType<typeof createTVChannelSchema>>;

interface TVChannelFormProps {
  onSuccess?: (data: CreateTVChannelRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateTVChannelRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const TVChannelForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: TVChannelFormProps) => {
  const { t } = useTranslation();
  const { branding } = useBranding();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categories || []);
  const showCountryField = branding.modules?.liveTvCountriesEnabled === true;
  const useCategoriesForTVChannelDropdown = () =>
    useCategoriesForDropdown('TV_CHANNEL');

  const { data: categoriesData } = useCategoriesForTVChannelDropdown();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    control
  } = useForm<TVChannelFormData>({
    resolver: zodResolver(createTVChannelSchema(t)),
    defaultValues: {
      channelNumber: initialData?.channelNumber || 0,
      countryId: initialData?.countryId || undefined,
      title: initialData?.title || '',
      tvGuideId: initialData?.tvGuideId || undefined,
      categories: initialData?.categories || [],
      epgId: initialData?.epgId || '',
      imageUrl: initialData?.imageUrl || '',
      active: initialData?.active ?? true,
      adultContent: initialData?.adultContent ?? false,
      kidsContent: initialData?.kidsContent ?? false,
      urlType: initialData?.urlType || 'EXTERNAL',
      url: initialData?.url || ''
    }
  });

  const urlTypeOptions = [
    { value: 'EXTERNAL', label: t.tvChannels.urlTypes.external },
    { value: 'INTERNAL', label: t.tvChannels.urlTypes.internal }
  ];

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  const mapTVGuidesToOptions = (data: unknown[]) =>
    (data as TVGuide[]).map(guide => ({ value: guide.id, label: guide.title }));

  const onSubmit = async (data: TVChannelFormData) => {
    setSubmitError(null);

    try {
      await onSuccess?.(data);
      if (!isEdit) {
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const watchedValues = watch(['active', 'adultContent', 'kidsContent', 'urlType', 'imageUrl']);
  const [active, adultContent, kidsContent, urlType, imageUrl] = watchedValues;

  const getUrlError = () => {
    if (errors.url) {
      if (urlType === 'EXTERNAL') {
        return t.tvChannels.validation.urlInvalidExternal;
      } else {
        return t.tvChannels.validation.urlInvalidInternal;
      }
    }
    return undefined;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? t.tvChannels.editTitle : t.tvChannels.createTitle}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? t.tvChannels.editDescription : t.tvChannels.createDescription}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex gap-6">
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label={t.tvChannels.fields.channelNumber}
                type="number"
                min="0"
                placeholder={t.tvChannels.placeholders.channelNumber}
                error={errors.channelNumber?.message}
                {...register('channelNumber', { valueAsNumber: true })}
              />

              {showCountryField && (
                <Controller
                  name="countryId"
                  control={control}
                  render={({ field }) => (
                    <CountryDropdown
                      label={t.tvChannels.fields.country}
                      placeholder={t.tvChannels.placeholders.country}
                      error={errors.countryId?.message}
                      disabled={isSubmitting}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
              )}
            </div>

            <Input
              label={t.tvChannels.fields.title}
              placeholder={t.tvChannels.placeholders.title}
              error={errors.title?.message}
              {...register('title')}
            />

            <ImageUrlUploadField
              label={t.tvChannels.fields.imageUrl}
              placeholder={t.tvChannels.placeholders.imageUrl}
              value={imageUrl}
              onChange={(url) => setValue('imageUrl', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.imageUrl?.message}
              disabled={isSubmitting}
              source={MediaSource.TV_LOGO}
              showPreview={false}
            />
          </div>
          <div className="flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Vista previa del canal"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                  target.style.display = 'block';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">del canal</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ApiSelect
            label={t.tvChannels.fields.tvGuide}
            placeholder={t.tvChannels.placeholders.tvGuide}
            useQueryHook={useTVGuidesForDropdown}
            mapDataToOptions={mapTVGuidesToOptions}
            error={errors.tvGuideId?.message}
            allowEmpty
            {...register('tvGuideId', { setValueAs: (v) => (v === '' ? undefined : v) })}
          />

          <MultiSelect
            label={t.tvChannels.fields.categories}
            placeholder={t.tvChannels.placeholders.categories}
            options={mapCategoriesToOptions(categoriesData || [])}
            value={selectedCategories}
            onChange={(newCategories) => {
              setSelectedCategories(newCategories);
              setValue('categories', newCategories);
            }}
            disabled={isSubmitting}
            error={errors.categories?.message}
          />

          <div className="md:col-span-2">
            <Input
              label={t.tvChannels.fields.epgId}
              placeholder={t.tvChannels.placeholders.epgId}
              error={errors.epgId?.message}
              helperText="Identificador único para la guía de programación electrónica"
              {...register('epgId')}
            />
          </div>

          <div className="md:col-span-2">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {t.tvChannels.fields.url}
              </label>

              <div className="flex space-x-6">
                {urlTypeOptions.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      value={option.value}
                      checked={urlType === option.value}
                      onChange={(e) => setValue('urlType', e.target.value as URLType)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>

              <Input
                type={urlType === 'EXTERNAL' ? 'url' : 'number'}
                placeholder={
                  urlType === 'EXTERNAL'
                    ? 'https://example.com/stream.m3u8'
                    : '12345'
                }
                error={getUrlError()}
                helperText={
                  urlType === 'EXTERNAL'
                    ? 'URL externa para el stream del canal'
                    : 'ID numérico interno del canal'
                }
                {...register('url')}
              />
            </div>
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Toggle
              label={t.tvChannels.fields.active}
              checked={active}
              onChange={(checked) => setValue('active', checked)}
              disabled={isSubmitting}
            />
            <Toggle
              label={t.tvChannels.fields.adultContent}
              checked={adultContent}
              onChange={(checked) => setValue('adultContent', checked)}
              disabled={isSubmitting}
            />
            <Toggle
              label={t.tvChannels.fields.kidsContent}
              checked={kidsContent}
              onChange={(checked) => setValue('kidsContent', checked)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t.tvChannels.actions.creating
              : isEdit
                ? 'Actualizar Canal de TV'
                : t.tvChannels.actions.create
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TVChannelForm;
